import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { API_URL } from '../config';
import { canImpersonate, beginImpersonation, homeForRole } from '../utils/impersonation';

// ============================================
// "VIEW AS" — open the portal as this person (super admin only)
// ============================================
// Support tool: reproduce exactly what a client or subbie is seeing without inventing test
// accounts. The backend hands back a REAL session for that user, so what you see — and anything
// you save — is genuinely theirs, and it is audited at both ends.
//
// Renders NOTHING unless the person looking at the screen is a super admin who is not already
// impersonating. `userId` is their `public.users.id`; pass nothing when the company has no login
// yet and the button simply won't appear.
// ============================================
export default function ViewAsButton({ userId, clientId, subcontractorId, name, small }) {
  const [busy, setBusy] = useState(false);
  const targetKey = userId || clientId || subcontractorId;
  if (!canImpersonate() || !targetKey) return null;

  const go = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/auth/impersonate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, client_id: clientId, subcontractor_id: subcontractorId })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error((data && data.error) || `Request failed: ${res.status}`);
      }

      beginImpersonation(data);
      toast.info(data.message || 'Viewing as another user');
      // A full reload, not a client-side navigate: every screen must re-read the swapped session.
      window.location.href = homeForRole(data.user.role);
    } catch (err) {
      console.error('View-as failed:', err);
      toast.error(err.message || 'Could not start viewing as that user');
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={go}
      disabled={busy}
      title={`View the portal as ${name || 'this user'}`}
      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border disabled:opacity-50"
      style={{ borderColor: '#CBD5E1', color: '#0A1929', backgroundColor: '#F8FAFC', cursor: busy ? 'wait' : 'pointer' }}
    >
      {busy ? 'Opening…' : (small ? '👁 View as' : '👁 View as this user')}
    </button>
  );
}
